import React, { useEffect, useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import '@/styles/aimup-redesign-feedback.css';

const CATEGORIES = [
  { value: 'idea', label: 'Verbesserungsidee' },
  { value: 'problem', label: 'Problem/Fehler' },
  { value: 'praise', label: 'Lob' },
  { value: 'other', label: 'Sonstiges' },
] as const;

const MAX_LENGTH = 500;
/** Signal der Navigation (Seitenleiste „Feedback“, Menü „Mehr“). */
export const OPEN_FEEDBACK_EVENT = 'aimup-open-feedback';

/** Feedback-Dialog nach Claude-Vorlage `layout_feedbackDialog_*`; geöffnet nur über die Navigation. */
export const FeedbackWidget: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const canSubmit = useMemo(() => !!category && message.trim().length > 0 && !sending, [category, message, sending]);

  useEffect(() => {
    const openDialog = () => setOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, openDialog);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, openDialog);
  }, []);

  useEffect(() => {
    if (!sent) return;
    const t = window.setTimeout(() => setSent(false), 2200);
    return () => window.clearTimeout(t);
  }, [sent]);

  if (!user) return null;

  const reset = () => {
    setCategory(null);
    setMessage('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-feedback', {
        body: { category, message: message.trim().slice(0, MAX_LENGTH) },
      });
      if (error || !(data as { success?: boolean } | null)?.success) {
        throw error ?? new Error('failed');
      }
      setOpen(false);
      reset();
      setSent(true);
    } catch {
      toast({
        title: 'Feedback konnte nicht gesendet werden.',
        description: 'Bitte versuche es später noch einmal.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <DialogPrimitive.Root
        open={open}
        onOpenChange={(next) => {
          if (sending) return;
          setOpen(next);
          if (!next) reset();
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="aur-fb-overlay" />
          <DialogPrimitive.Content className="aur-fb-dialog" aria-describedby="aur-fb-desc">
            <div className="aur-fb-head">
              <div>
                <DialogPrimitive.Title className="aur-fb-title">Feedback an AimUp</DialogPrimitive.Title>
                <DialogPrimitive.Description id="aur-fb-desc" className="aur-fb-desc">
                  Hilf uns, AimUp noch besser zu machen.
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close className="aur-fb-close" aria-label="Schließen" disabled={sending}>✕</DialogPrimitive.Close>
            </div>

            <div className="aur-fb-categories">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  className={`aur-fb-category${category === c.value ? ' is-selected' : ''}`}
                  aria-pressed={category === c.value}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="aur-fb-field">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                placeholder="Deine Nachricht …"
                rows={5}
                className="aur-fb-text"
              />
              <span className="aur-fb-count">{message.length}/{MAX_LENGTH}</span>
            </div>

            <button type="button" disabled={!canSubmit} onClick={handleSubmit} className="aur-fb-send">
              {sending ? 'Wird gesendet …' : 'Feedback senden'}
            </button>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      {sent && <div className="aur-fb-sent" role="status">Danke, dein Feedback wurde gesendet.</div>}
    </>
  );
};
